/**
 * DayCard — the ONE card a campus-day gets on the Production Records
 * timeline (PROD-1279 part two). Its body is the same everywhere; only the
 * identity slot changes: the day on a single-campus school ("Today",
 * "Yesterday", else "Thu, Sep 17"), the campus name under a day header on a
 * multi-campus one.
 *
 * Quiet when complete, loud when open: what still needs doing is what
 * stands out. A complete day is a compact row that sits flat on the panel
 * (no shadow, a softer border, a medium-weight title, the light check
 * glyph): the mealtimes, who signed and when, then View details and Print.
 * An open day is raised (a drop shadow, the full border, a semibold title)
 * and expands: the progress ring with its percent, the status in words
 * ("67% complete") with the count behind it ("2 of 3 entries done"), and
 * ONE primary action (Get started / Continue) beside a quiet Print for the
 * blank hand-fill forms. Neither card is dimmed: both stay white and fully
 * legible on the grey panel.
 *
 * The only disabled state left is a day whose records the materializer has
 * not generated yet: the ring is dashed, the detail names why, and the
 * action is disabled rather than a dead primary.
 */

import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Printer } from 'lucide-react'

import { DayStatusGlyph } from '@/components/production/day-status-glyph'
import type { DayProgress } from '@/lib/production-records/day-progress'

export interface DayCardProps {
  /** The card's identity: the day ("Today", "Yesterday", "Thu, Sep 17") on a single campus, the campus under a day header. */
  title: string
  progress: DayProgress
  /** "Breakfast · Lunch" */
  mealtimesLine: string
  /** "Signed yesterday by Shannon Talmage"; null while nothing is signed. */
  signedLine: string | null
  /** Get started / Continue: opens the unified record for this campus-day. */
  onOpen: () => void
  /** View details: the day's record page. Omitted when the day has no record to show. */
  onView?: (() => void) | null
  onPrint: () => void
  /** Campus cards under a day header read one size down. */
  dense?: boolean
  id?: string
}

export function DayCard({
  title,
  progress,
  mealtimesLine,
  signedLine,
  onOpen,
  onView = null,
  onPrint,
  dense = false,
  id,
}: DayCardProps) {
  // Weight and elevation, not colour, say which card wants attention: the
  // open one is raised and semibold, the complete one flat and medium.
  const heading = (
    <h3
      className={cn(
        'tracking-normal',
        progress.complete ? 'font-medium' : 'font-semibold',
        dense ? 'text-[15px]' : 'text-base',
      )}>
      {title}
    </h3>
  )

  if (progress.complete) {
    return (
      <Card
        id={id}
        role="group"
        aria-label={`${title}, complete`}
        data-state="complete"
        className={cn('flex items-center gap-3.5 border-border/60 shadow-none', dense ? 'p-3' : 'p-3.5')}>
        <DayStatusGlyph progress={progress} size={dense ? 'sm' : 'md'} />
        <div className="min-w-0 flex-1">
          {heading}
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-muted-foreground">
            <span>{mealtimesLine}</span>
            {signedLine ? (
              <>
                <span aria-hidden className="text-border">
                  ·
                </span>
                <span className="font-medium text-success-foreground">{signedLine}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onView ? (
            <Button type="button" size="sm" variant="secondary" onClick={onView}>
              View details
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="outline" onClick={onPrint}>
            <Printer aria-hidden />
            Print
          </Button>
        </div>
      </Card>
    )
  }

  const action = progress.action
  return (
    <Card
      id={id}
      role="group"
      aria-label={`${title}, ${progress.statusText.toLowerCase()}`}
      data-state={progress.awaitingRecords ? 'awaiting' : 'open'}
      className={cn('flex items-start gap-3.5 border-border shadow-md', dense ? 'p-3.5 pl-3' : 'p-4 pl-3.5')}>
      <DayStatusGlyph progress={progress} size={dense ? 'sm' : 'md'} />
      <div className="min-w-0 flex-1 space-y-2">
        {heading}
        <p className="-mt-1 flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-semibold text-orange-700 dark:text-orange-400">{progress.statusText}</span>
          {progress.statusDetail ? (
            <span className="text-[13px] text-muted-foreground">{progress.statusDetail}</span>
          ) : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-center">
        <Button type="button" size="icon" variant="outline" aria-label="Print blank forms" onClick={onPrint}>
          <Printer aria-hidden />
        </Button>
        {action ? (
          <Button
            type="button"
            disabled={!action.enabled}
            onClick={onOpen}
            className={cn('shrink-0', !action.enabled && 'opacity-55')}>
            {action.label}
          </Button>
        ) : null}
      </div>
    </Card>
  )
}
